export type ShareReceiptResult =
  | "shared"
  | "downloaded"
  | "cancelled"
  | "skipped";

export const isAbortError = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  "name" in err &&
  (err as { name: string }).name === "AbortError";

/** Phone/tablet share sheet. Desktop Chrome may expose canShare; skip it. */
export const isMobileShareSurface = (): boolean => {
  const matchMedia = globalThis.matchMedia;
  if (typeof matchMedia !== "function") return false;
  try {
    return matchMedia("(hover: none) and (pointer: coarse)").matches;
  } catch {
    return false;
  }
};

/** Probe Web Share for a file of the given media type. */
export const canShareFilesNatively = (
  mediaType = "application/pdf"
): boolean => {
  if (typeof navigator.canShare !== "function") return false;
  try {
    const probe = new File([], "probe", { type: mediaType });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

/** Native sheet only on a coarse-pointer device that can share files. */
export const shouldOfferNativeShare = (
  mediaType = "application/pdf"
): boolean => isMobileShareSurface() && canShareFilesNatively(mediaType);

export const downloadBlob = (blob: Blob, fileName: string): void => {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(href);
};

/** Share via the OS sheet on mobile; download on desktop. */
export const shareOrDownloadBlob = async ({
  blob,
  fileName,
  title,
  mediaType,
}: {
  blob: Blob;
  fileName: string;
  title: string;
  mediaType: string;
}): Promise<ShareReceiptResult> => {
  const file = new File([blob], fileName, { type: mediaType });

  if (shouldOfferNativeShare(mediaType) && typeof navigator.share === "function") {
    try {
      await navigator.share({ files: [file], title });
      return "shared";
    } catch (err) {
      if (isAbortError(err)) return "cancelled";
    }
  }

  downloadBlob(blob, fileName);
  return "downloaded";
};
